import { type Page, useContentConfig } from '@ad-lucy/context/adData';
import { useBreakpoint } from '@ad-lucy/hooks/sizing/useBreakpoint';
import {
  createContext,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Swiper } from 'swiper/types';

import type { PagesContextType, PagesProviderProps } from './types';

export const PagesContext = createContext<PagesContextType>({} as PagesContextType);
export const usePages = (): PagesContextType => useContext(PagesContext);

export function PagesProvider({ children }: PagesProviderProps): ReactElement {
  const breakpoint = useBreakpoint();
  const { data: contentConfig } = useContentConfig();

  const [pages, setPages] = useState<Page[]>([]);
  const [currentPage, setCurrentPage] = useState<Page>();
  const [swiper, setSwiper] = useState<Swiper>();

  const getActualSwiperIndex = useCallback((): number => {
    return swiper?.params?.loop ? (swiper?.realIndex ?? 0) : (swiper?.activeIndex ?? 0);
  }, [swiper]);

  // Set current page after swiper initialization if not set yet.
  useEffect(() => {
    if (!currentPage && swiper && pages[getActualSwiperIndex()]) {
      setCurrentPage(pages[getActualSwiperIndex()]);
    }
  }, [swiper, currentPage, pages, getActualSwiperIndex]);

  // Sync current page with active slide of swiper.
  useEffect(() => {
    const onRealIndexChange = (event: Swiper): void => {
      const page = pages[event.realIndex % pages.length]; // In case the pages are duplicated
      if (!page) return;
      setCurrentPage(page);
    };

    const onActiveIndexChange = (event: Swiper): void => {
      const page = pages[event.activeIndex % pages.length]; // In case the pages are duplicated
      if (!page) return;
      setCurrentPage(page);
    };

    if (Array.isArray(pages) && pages.length && swiper?.params?.loop) {
      swiper.on('realIndexChange', onRealIndexChange);
    }
    if (Array.isArray(pages) && pages.length && !swiper?.params?.loop) {
      swiper?.on('activeIndexChange', onActiveIndexChange);
    }

    return () => {
      swiper?.off('realIndexChange', onRealIndexChange);
      swiper?.off('activeIndexChange', onActiveIndexChange);
    };
  }, [swiper, pages]);

  const setCurrentPageByIndex = useCallback(
    (index: number) => {
      setCurrentPage(pages[index]);
      // Change active slide of swiper if swiper is set.
      if (swiper?.params?.loop) {
        swiper?.slideToLoop(index);
      } else {
        swiper?.slideTo(index);
      }
    },
    [swiper, pages],
  );

  const currentPageIndex = useMemo(() => {
    if (!pages || pages.length === 0) {
      return 0;
    }

    const index = pages.findIndex((page) => page.id === currentPage?.id);

    return index !== -1 ? index : 0;
  }, [pages, currentPage]);

  const pagesContextValue: PagesContextType = useMemo(
    () => ({
      pages,
      currentPage,
      setCurrentPageByIndex,
      currentPageIndex,
      swiper,
      setSwiper,
      getActualSwiperIndex,
    }),
    [pages, currentPage, setCurrentPageByIndex, currentPageIndex, swiper, getActualSwiperIndex],
  );

  useEffect(() => {
    if (contentConfig) {
      let pages: Page[];
      if (breakpoint) {
        pages = contentConfig.pages?.filter(
          (page) => !page.grid.breakpoint || page.grid.breakpoint === breakpoint,
        );
      } else {
        pages = contentConfig.pages;
      }
      setPages(pages);
      if (swiper) {
        setCurrentPage(pages[getActualSwiperIndex()]);
      }
    }
  }, [breakpoint, contentConfig, getActualSwiperIndex, swiper]);

  return <PagesContext.Provider value={pagesContextValue}>{children}</PagesContext.Provider>;
}
